/**
 * SeparateAdministrationList — alphabetical list of drugs flagged for
 * separate administration (own line, no Y-site sharing).
 *
 * Source-PDF concept (USB LL0034-V07 §"ARZNEIMITTEL ZUR SEPARATEN
 * VERABREICHUNG"). Derived from the matrix's blanket verdict against the
 * `(any other drug)` sentinel — see `separateAdministration.ts` for the
 * single source of truth.
 *
 * Selection mirrors the matrix: clicking a card sets the drug axis slot
 * (same `onSelectDrug` callback the matrix uses), so the existing
 * DrugSidePanel + auto-fire flow lights up unchanged.
 */

import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { CompatibilityBoardState, CompatibilityDrug } from '@frontend/components/ysite/types'
import { VERDICT_TONE } from '@frontend/components/ysite/types'
import { splitLabel } from '@frontend/components/ysite/label'
import { getSeparateAdminDrugs } from './separateAdministration'
import { TONE_TEXT } from '@frontend/components/ysite/verdict.ui'

interface SeparateAdministrationListProps {
  state: CompatibilityBoardState
  selectedDrugId: string | null
  onSelectDrug: (drug: CompatibilityDrug) => void
}

export function SeparateAdministrationList({
  state,
  selectedDrugId,
  onSelectDrug,
}: SeparateAdministrationListProps) {
  const { t } = useTranslation()
  const entries = getSeparateAdminDrugs(state)

  if (entries.length === 0) {
    return (
      <p className="text-[13px] text-neutral-500">
        {t(
          'compatibility.separate.emptyTitle',
          'No drugs in this matrix require separate administration.',
        )}
      </p>
    )
  }

  return (
    // `min-h-0 flex-1` lets the list shrink inside the left column's flex
    // column; `overflow-y-auto` puts the scrollbar INSIDE this container
    // instead of on the page. Mirrors the matrix/carriers tabs where the
    // grid's own overflow contains the scroll — that's what keeps the
    // sidebar (a flex sibling at the board level) in place while the
    // user scrolls a long list.
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-y-auto">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-display text-[21px] font-semibold tracking-[-0.3px] text-neutral-900">
          {t('compatibility.separate.title', 'Drugs for separate administration')}
        </h2>
        <span className="text-[13px] text-neutral-500">{entries.length}</span>
      </div>

      <ul className="flex flex-col gap-2">
        {entries.map(({ drug, cell }) => {
          const tone = VERDICT_TONE[cell.verdict]
          const { name, concentration } = splitLabel(drug.label)
          const active = drug.id === selectedDrugId
          return (
            <li key={drug.id}>
              <button
                type="button"
                onClick={() => onSelectDrug(drug)}
                aria-pressed={active}
                aria-label={t(
                  'compatibility.separate.selectAria',
                  '{{drug}} — {{verdict}}',
                  {
                    drug: drug.label,
                    verdict: t(`compatibility.verdicts.${cell.verdict}`),
                  },
                )}
                className={cn(
                  'flex w-full items-start gap-3 rounded-[12px] border bg-[var(--canvas)] px-4 py-3 text-left transition',
                  'active:scale-[0.99] hover:border-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-focus',
                  active ? 'border-primary-500' : 'border-[var(--divider)]',
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    'mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full',
                    `bg-tone-${tone === 'muted' ? 'mute' : tone}`,
                  )}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-display text-[15px] font-semibold text-neutral-900">
                    {name || drug.label}
                  </span>
                  {concentration && (
                    <span className="block truncate text-[12px] text-neutral-600">
                      {concentration}
                    </span>
                  )}
                  {drug.atc && (
                    <span className="mt-1 block truncate font-mono text-[11px] text-neutral-500">
                      {drug.atc}
                    </span>
                  )}
                  {/* Phone: the verdict wraps UNDER the name so the drug name
                      never has to truncate to make room for it. From sm+ it
                      floats to the right (the desktop/iPad layout) instead. */}
                  <span
                    className={cn(
                      'mt-1.5 block text-[12px] font-semibold uppercase tracking-[0.04em] sm:hidden',
                      TONE_TEXT[tone],
                    )}
                  >
                    {t(`compatibility.verdicts.${cell.verdict}`)}
                  </span>
                </span>
                <span
                  className={cn(
                    'hidden flex-shrink-0 self-center whitespace-nowrap text-[12px] font-semibold uppercase tracking-[0.04em] sm:block',
                    TONE_TEXT[tone],
                  )}
                >
                  {t(`compatibility.verdicts.${cell.verdict}`)}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
